import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PopoverMenu } from '@/components/PopoverMenu/PopoverMenu';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { formatDate, FULL_DATE_TIME_FORMAT } from '@/helpers/dateHelpers/dateHelpers';
import { useUser } from '@/context/userContext';
import { getDashboardPath } from '@/bundle/_OrgAdmin/pages/DashboardPage/path/path';
import {
  DASHBOARD_QUERY_KEYS,
  loadNotifications,
  loadUnreadCount,
  markAllRead,
  markRead,
} from '@/bundle/_OrgAdmin/pages/DashboardPage/api/api';
import { NOTIFICATIONS_POLL_MS } from '@/bundle/_OrgAdmin/pages/DashboardPage/const/const';
import { NotificationType } from '@/types/dashboardTypes';
import { Badge, BellIcon, NotificationRow, NotificationsHeader, NotificationsList } from './styles';
import { TasksStatus } from '@/bundle/_OrgAdmin/pages/DashboardPage/ui/TasksStatus';
import { StatusWrapper } from '@/components/StatusWrapper/StatusWrapper';
import { STATUS_VARIANT_HIGHLIGHT, StatusVariantType } from '@/components/StatusWrapper/const';
import { IconButton } from '@/components/IconButton/IconButton';

const KIND_STATUS_MAP: Record<NotificationType['kind'], { title: string; variant: StatusVariantType }> = {
  assigned: { title: 'Assigned', variant: STATUS_VARIANT_HIGHLIGHT.YELLOW },
  started: { title: 'Started', variant: STATUS_VARIANT_HIGHLIGHT.BLUE },
  question: { title: 'Question', variant: STATUS_VARIANT_HIGHLIGHT.RED },
  answered: { title: 'Reply', variant: STATUS_VARIANT_HIGHLIGHT.BLUE },
  completed: { title: 'Completed', variant: STATUS_VARIANT_HIGHLIGHT.GREEN },
  reassigned: { title: 'Reassigned', variant: STATUS_VARIANT_HIGHLIGHT.YELLOW },
  closed: { title: 'Closed', variant: STATUS_VARIANT_HIGHLIGHT.GREY },
  message: { title: 'Message', variant: STATUS_VARIANT_HIGHLIGHT.BLUE },
} as const;

export const NotificationsBell = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasOrganizationRole, user } = useUser();
  const [isOpen, setIsOpen] = useState(false);

  // light poll for the badge; the full list loads only while the dropdown is open
  const { data: unreadResponse } = useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.unread,
    queryFn: loadUnreadCount,
    enabled: hasOrganizationRole && !!user,
    refetchInterval: NOTIFICATIONS_POLL_MS,
    refetchIntervalInBackground: false,
  });

  const { data: listResponse } = useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.notifications,
    queryFn: loadNotifications,
    enabled: hasOrganizationRole && isOpen,
  });

  const unread = unreadResponse?.body?.unread ?? 0;
  const rows = listResponse?.body?.results ?? [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.unread });
    queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.notifications });
    queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEYS.tasks] });
  };

  const { mutate: readAll } = useMutation({
    mutationKey: ['notifications_read_all'],
    mutationFn: markAllRead,
    onSuccess: invalidate,
  });
  const { mutate: readOne } = useMutation({
    mutationKey: ['notifications_read'],
    mutationFn: (id: string) => markRead([id]),
    onSuccess: invalidate,
  });

  const open = (row: NotificationType) => {
    if (!row.is_read) readOne(row.id);
    setIsOpen(false);
    navigate(`${getDashboardPath()}?task=${row.task}`);
  };

  if (!hasOrganizationRole) return null;

  return (
    <PopoverMenu
      isOpen={isOpen}
      onToggleOpen={setIsOpen}
      placement='bottom-start'
      anchorSlot={
        <IconButton aria-label='Notifications' aria-expanded={isOpen} width='36px' height='36px' svgPath='stroke'>
          <BellIcon />
          {unread > 0 && <Badge>{unread > 99 ? '99+' : unread}</Badge>}
        </IconButton>
      }
    >
      <NotificationsHeader>
        <Typography variant='bodySmall' fontWeight='semibold'>
          Notifications
        </Typography>
        {unread > 0 && (
          <button type='button' className='link' onClick={() => readAll()}>
            Mark all read
          </button>
        )}
      </NotificationsHeader>
      <NotificationsList>
        {rows.length === 0 && (
          <Box padding='16px'>
            <Typography variant='bodySmall'>Nothing new.</Typography>
          </Box>
        )}
        {rows.map((row) => (
          <NotificationRow key={row.id} type='button' $unread={!row.is_read} onClick={() => open(row)}>
            <Box display='flex' justifyContent='space-between' alignItems='center' gap='8px'>
              <StatusWrapper variant={KIND_STATUS_MAP[row.kind]?.variant}>
                {KIND_STATUS_MAP[row.kind]?.title}
              </StatusWrapper>
              <Typography variant='caption'>{formatDate(row.date_created, FULL_DATE_TIME_FORMAT)}</Typography>
            </Box>
            <Typography variant='bodySmall'>{row.text}</Typography>
            <TasksStatus status={row.task_status}>{row.task_title}</TasksStatus>
          </NotificationRow>
        ))}
      </NotificationsList>
    </PopoverMenu>
  );
};
